"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import {blog14} from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

export default function Blog14() {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Teaching in the Age of Intelligence: Why AI- LMS Is a Game-Changer for Educators"
                  date="October 1 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog14}
                alt="Teaching in the Age of Intelligence feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={14} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          As an educator, I’ve spent years navigating the evolving landscape of teaching—chalkboards to smartboards, textbooks to tablets, and now, the rise of artificial intelligence in education. Among the most exciting developments is the emergence of AI-powered LXPs (Learning eXperience Platforms). These platforms aren’t just digital filing cabinets or grading tools—they’re intelligent partners in pedagogy, designed to enhance how we teach, support how students learn, and streamline the administrative burdens that often weigh us down.
        </IntroParagraph>
        <IntroParagraph>
          In this blog, I want to share why LXP has become an indispensable part of my teaching toolkit, and why I believe it’s poised to redefine education for instructors across disciplines.
        </IntroParagraph>
      </IntroSection>

      <ContentSection title="Personalized Learning Without the Overhead">
        <ContentParagraph>
          One of the most powerful features of LXP is its ability to personalize learning at scale. In a traditional classroom, tailoring instruction to each student’s needs is ideal—but often impractical. With LXP, adaptive learning paths are no longer a luxury. The system analyzes student performance, engagement patterns, and learning preferences to automatically adjust content delivery.
        </ContentParagraph>
        <ContentParagraph>
          For example, if a student struggles with a concept in calculus, the LMS can recommend targeted practice problems, video tutorials, or even peer discussion groups. Conversely, students who master material quickly can be offered enrichment activities or advanced modules. As an instructor, I don’t have to manually track every learner’s journey—the system does it for me, allowing me to focus on mentoring and deeper instruction.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Automation That Gives Me Time Back">
        <ContentParagraph>
          Let’s talk about grading. It’s essential, but it’s also time-consuming and often repetitive. LXP platforms automate grading for quizzes, assignments, and even short-answer responses using natural language processing. I was initially skeptical—could a machine really evaluate student work fairly? But after testing it, I found the AI-generated feedback to be surprisingly nuanced and consistent.
        </ContentParagraph>
        <ContentParagraph>
          Beyond grading, the LMS handles scheduling, reminders, and even attendance tracking. I no longer spend hours setting up calendars or chasing down late submissions. The system nudges students automatically, freeing me to invest that time in lesson planning or one-on-one support.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Data-Driven Insights That Actually Matter">
        <ContentParagraph>
          One of the most underutilized tools in education is data. We collect it constantly—grades, attendance, participation—but rarely have the time or tools to interpret it meaningfully. LXP changes that. Its analytics dashboard gives me real-time insights into student engagement, performance trends, and even emotional sentiment (based on discussion tone and feedback).
        </ContentParagraph>
        <ContentParagraph>
          This means I can identify at-risk students before they fail an exam. I can see which modules are underperforming and revise them. I can even compare outcomes across semesters to refine my teaching strategies. It’s like having a research assistant who never sleeps, constantly surfacing actionable insights.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Smarter Content Recommendations">
        <ContentParagraph>
          Another benefit I’ve come to appreciate is the LMS’s ability to recommend content—not just to students, but to me. Based on course objectives and student behavior, the system suggests articles, videos, simulations, and even open educational resources that align with my curriculum. It’s like having a librarian and instructional designer rolled into one.
        </ContentParagraph>
        <ContentParagraph>
          This has been especially helpful in interdisciplinary courses, where I’m not always the subject matter expert in every topic. The AI helps me curate high-quality materials that enrich the learning experience without hours of searching.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Enhanced Student Support">
        <ContentParagraph>
          Students today expect instant answers and 24/7 support. While I can’t be available around the clock, my LXP can. The built-in chatbot handles routine questions—“When is the assignment due?” “How do I access the reading?”—with speed and accuracy. It also guides students through technical issues, freeing up my inbox from dozens of repetitive queries.
        </ContentParagraph>
        <ContentParagraph>
          More importantly, the AI assistant is multilingual, which has been a game-changer for my international students. They feel more supported, more confident, and more engaged. And when the system detects confusion or frustration, it flags me so I can intervene personally.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Academic Integrity and Security">
        <ContentParagraph>
          As educators, we’re rightly concerned about plagiarism and data privacy. LXP platforms come equipped with plagiarism detection tools that go beyond simple text matching. They analyze writing style, originality, and even cross-reference with previous submissions. This helps me maintain academic integrity without playing detective.
        </ContentParagraph>
        <ContentParagraph>
          On the privacy front, the system complies with FERPA and GDPR standards, encrypts student data, and offers role-based access controls. I can rest easy knowing that sensitive information is protected—and that I’m not inadvertently violating compliance rules.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="A Partner in Pedagogy">
        <ContentParagraph>
          Whether I’m teaching in-person, online, or hybrid, the LXP adapts. It’s mobile-friendly, integrates with Zoom and Microsoft Teams, and supports multimedia content. I’ve used it to host live lectures, asynchronous modules, and even flipped classrooms. The flexibility is unmatched.
        </ContentParagraph>
        <ContentParagraph>
          For my colleagues in vocational training, STEM, humanities, and the arts, the LMS offers customizable templates and tools that fit their unique needs. It’s not a one-size-fits-all solution—it’s a platform that molds itself to your pedagogy.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="A Partner in Pedagogy">
        <ContentParagraph>
          Perhaps the most surprising benefit of LXP is how it’s changed my relationship with teaching. It’s not just a tool—it’s a partner. It helps me reflect, iterate, and grow. It challenges me to think differently about how I deliver content, assess learning, and support students.
        </ContentParagraph>
        <ContentParagraph>
          I’ve found myself experimenting more, taking risks, and embracing innovation. And my students have responded with higher engagement, better outcomes, and more meaningful feedback.
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="Final Thoughts">
        <ContentParagraph>
          LXP isn’t about replacing educators—it’s about empowering us. It handles the heavy lifting so we can focus on what we do best: inspiring minds, fostering curiosity, and shaping futures. In a world where education is evolving rapidly, LXP gives us the tools to lead that evolution—not just follow it.
        </ContentParagraph>
        <ContentParagraph>
          If you’re an educator wondering whether LXP is worth the investment, I say this: try it. Explore it. Challenge it. You might just find that it transforms not only your classroom—but your entire approach to teaching.
        </ContentParagraph>
      </ContentSection>

    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;
